import React, {
  useState, useEffect, useRef, useCallback, useMemo,
} from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import MusicPlayerWidget, { EqualizerBars } from './MusicPlayerWidget';
import { writeFetch } from '../../api';
import { requestPresignedDownloadUrl } from '../../services/s3UploadService';

const {
  FiMusic, FiWind, FiSun, FiDroplet, FiFeather,
  FiMoon, FiChevronUp, FiChevronDown, FiHeadphones, FiCheckCircle,
  FiRefreshCw, FiAlertCircle,
} = FiIcons;

const TABLE = 'soundscape_tracks_1776430731351';

/* ─────────────────────────────────────────────────────────────────────────
   S3 URL helpers
   • Public S3 URLs (*.s3.amazonaws.com/* without X-Amz-Signature) → use as-is
   • Pre-signed URLs (contain X-Amz-Signature) → refresh via server before play
   • Fallback CDN URLs (pixabay / non-S3) → use as-is
───────────────────────────────────────────────────────────────────────── */
function isPresignedUrl(url = '') {
  return url.includes('X-Amz-Signature') || url.includes('x-amz-signature');
}

function isS3Url(url = '') {
  return url.includes('.s3.') || url.includes('s3.amazonaws.com');
}

/**
 * resolvePlayableUrl
 * Given a track object, returns the best URL to hand to the <audio> element.
 *  - If the stored URL is a pre-signed S3 URL → fetch a fresh one via the server
 *  - If it is a plain public S3 URL → use directly
 *  - If it is a CDN / external URL → use directly
 */
async function resolvePlayableUrl(track, ownerId) {
  const stored = track.src || '';

  if (!stored) throw new Error('Track has no audio source.');

  /* Pre-signed URL — always refresh to avoid expiry */
  if (isPresignedUrl(stored) && track._raw?.s3_key) {
    try {
      const fresh = await requestPresignedDownloadUrl({
        s3Key:   track._raw.s3_key,
        ownerId: ownerId || 'admin',
      });
      return fresh;
    } catch (err) {
      console.warn('[SoundscapePlayer] Pre-sign refresh failed, falling back to stored URL:', err.message);
      return stored; // best-effort fallback
    }
  }

  /* Public S3 or external CDN — use directly */
  return stored;
}

/* ─────────────────────────────────────────────────────────────────────────
   CATEGORY META
───────────────────────────────────────────────────────────────────────── */
const CATEGORY_META = {
  hymns:         { icon: FiMusic,     color: 'text-rose-500',    bg: 'bg-rose-50'    },
  instrumentals: { icon: FiHeadphones,color: 'text-sky-500',     bg: 'bg-sky-50'     },
  ambience:      { icon: FiWind,      color: 'text-emerald-500', bg: 'bg-emerald-50' },
};

const TRACK_STYLE_CYCLE = {
  hymns: [
    { barColor: 'bg-rose-400',   color: 'text-rose-400',   bg: 'bg-rose-50',   icon: FiMusic    },
    { barColor: 'bg-purple-400', color: 'text-purple-400', bg: 'bg-purple-50', icon: FiFeather  },
    { barColor: 'bg-pink-400',   color: 'text-pink-400',   bg: 'bg-pink-50',   icon: FiMusic    },
  ],
  instrumentals: [
    { barColor: 'bg-amber-400',  color: 'text-amber-400',  bg: 'bg-amber-50',  icon: FiSun      },
    { barColor: 'bg-indigo-400', color: 'text-indigo-400', bg: 'bg-indigo-50', icon: FiMoon     },
    { barColor: 'bg-orange-400', color: 'text-orange-400', bg: 'bg-orange-50', icon: FiFeather  },
  ],
  ambience: [
    { barColor: 'bg-emerald-500',color: 'text-emerald-500',bg: 'bg-emerald-50',icon: FiDroplet  },
    { barColor: 'bg-sky-400',    color: 'text-sky-400',    bg: 'bg-sky-50',    icon: FiWind     },
    { barColor: 'bg-teal-400',   color: 'text-teal-400',   bg: 'bg-teal-50',   icon: FiDroplet  },
  ],
};

const DEFAULT_STYLE = { barColor: 'bg-accent-gold', color: 'text-accent-gold', bg: 'bg-amber-50', icon: FiMusic };

/* ─────────────────────────────────────────────────────────────────────────
   FALLBACK TRACKS — used when DB has no active tracks
───────────────────────────────────────────────────────────────────────── */
const FALLBACK_TRACKS = [
  { id: 'hymn-piano',      label: 'Soft Piano Hymn',   description: 'Gentle, peaceful piano melodies',   category: 'hymns',         src: 'https://cdn.pixabay.com/audio/2022/01/18/audio_d1718ab41b.mp3' },
  { id: 'hymn-strings',    label: 'Gentle Strings',    description: 'Warm cello & violin harmonics',     category: 'hymns',         src: 'https://cdn.pixabay.com/audio/2023/06/12/audio_8cb749b7c5.mp3' },
  { id: 'inst-meditation', label: 'Meditation Bells',  description: 'Tibetan singing bowls & stillness', category: 'instrumentals', src: 'https://cdn.pixabay.com/audio/2021/10/25/audio_5bfe8f8f4e.mp3' },
  { id: 'amb-rain',        label: 'Forest Rain',       description: 'Calming rain & birdsong',           category: 'ambience',      src: 'https://cdn.pixabay.com/audio/2022/03/10/audio_c8c8a73467.mp3' },
  { id: 'amb-ocean',       label: 'Ocean Waves',       description: 'Soft coastal waves & sea breeze',   category: 'ambience',      src: 'https://cdn.pixabay.com/audio/2021/09/06/audio_6fa1a3c84f.mp3' },
];

/* ─────────────────────────────────────────────────────────────────────────
   mapDbTrack
───────────────────────────────────────────────────────────────────────── */
function mapDbTrack(row, indexWithinCategory) {
  const cat   = row.category || 'ambience';
  const cycle = TRACK_STYLE_CYCLE[cat] || [];
  const style = cycle[indexWithinCategory % cycle.length] || DEFAULT_STYLE;
  return {
    id:          row.id,
    label:       row.label       || row.file_name || 'Untitled Track',
    description: row.description || '',
    category:    cat,
    src:         row.s3_url      || '',   // raw stored URL (may be pre-signed or public)
    icon:        style.icon,
    barColor:    style.barColor,
    color:       style.color,
    bg:          style.bg,
    _raw:        row,                     // keep original DB row for s3_key access
  };
}

/* ─────────────────────────────────────────────────────────────────────────
   buildCategories
───────────────────────────────────────────────────────────────────────── */
function buildCategories(tracks) {
  const CAT_ORDER = ['hymns', 'instrumentals', 'ambience'];
  const grouped   = {};

  tracks.forEach(t => {
    const c = t.category || 'ambience';
    if (!grouped[c]) grouped[c] = [];
    grouped[c].push(t);
  });

  const result = CAT_ORDER
    .filter(id => grouped[id]?.length > 0)
    .map(id => ({
      id,
      label:  id.charAt(0).toUpperCase() + id.slice(1),
      icon:   CATEGORY_META[id]?.icon  || FiMusic,
      color:  CATEGORY_META[id]?.color || 'text-accent-gold',
      bg:     CATEGORY_META[id]?.bg    || 'bg-amber-50',
      tracks: grouped[id],
    }));

  Object.keys(grouped).forEach(id => {
    if (!CAT_ORDER.includes(id)) {
      result.push({
        id,
        label:  id.charAt(0).toUpperCase() + id.slice(1),
        icon:   FiMusic,
        color:  'text-accent-gold',
        bg:     'bg-amber-50',
        tracks: grouped[id],
      });
    }
  });

  return result;
}

/* ─────────────────────────────────────────────────────────────────────────
   PERSISTENCE
───────────────────────────────────────────────────────────────────────── */
const prefKey = (id) => `soundscape_pref_${id || 'global'}`;

function loadPrefs(memId) {
  try {
    const raw = localStorage.getItem(prefKey(memId));
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

function savePrefs(memId, trackId, vol) {
  try {
    localStorage.setItem(prefKey(memId), JSON.stringify({ trackId, volume: vol, savedAt: Date.now() }));
  } catch { /* quota */ }
}

/* ─────────────────────────────────────────────────────────────────────────
   SOUNDSCAPE PLAYER
───────────────────────────────────────────────────────────────────────── */
const SoundscapePlayer = ({ preferred, memorialId, ownerId }) => {
  const [trackCategories, setTrackCategories] = useState([]);
  const [allTracks,       setAllTracks]       = useState([]);
  const [fetchStatus,     setFetchStatus]     = useState('loading');

  const [active,         setActive]         = useState(null);
  const [volume,         setVolume]         = useState(() => loadPrefs(memorialId)?.volume ?? 0.35);
  const [isPlaying,      setIsPlaying]      = useState(false);
  const [isLoading,      setIsLoading]      = useState(false);
  const [loadError,      setLoadError]      = useState(false);
  const [expanded,       setExpanded]       = useState(false);
  const [activeCategory, setActiveCategory] = useState('');
  const [savedBadge,     setSavedBadge]     = useState(false);

  /* Resolved playable URL cache: trackId → url */
  const urlCache = useRef({});

  const audioRef    = useRef(null);
  const fadeRef     = useRef(null);
  const volTimerRef = useRef(null);
  const volumeRef   = useRef(volume);
  const fadingRef   = useRef(false);

  useEffect(() => { volumeRef.current = volume; }, [volume]);

  /* ── Resolve a playable URL for a track (with caching) ── */
  const getPlayableUrl = useCallback(async (track) => {
    if (!track?.src) throw new Error('No audio source for this track.');

    /* Cache: avoid repeated pre-sign calls for the same track in one session.
       For pre-signed URLs, cache for max 10 min (they're valid ~15 min from server). */
    const cached = urlCache.current[track.id];
    if (cached && Date.now() - cached.ts < 10 * 60 * 1000) {
      return cached.url;
    }

    const url = await resolvePlayableUrl(track, ownerId || 'admin');
    urlCache.current[track.id] = { url, ts: Date.now() };
    return url;
  }, [ownerId]);

  /* ── Fetch tracks from DB ── */
  const fetchTracks = useCallback(async () => {
    setFetchStatus('loading');
    try {
      const res  = await writeFetch(`read/${TABLE}?sort=sort_order&order=asc&limit=200`);
      const data = await res.json();
      const rows = Array.isArray(data.data) ? data.data : [];

      const active_rows = rows.filter(r =>
        (r.is_active === 1 || r.is_active === '1') && r.s3_url
      );

      if (active_rows.length === 0) {
        const enriched = FALLBACK_TRACKS.map((t, i) => ({
          ...t,
          ...mapDbTrack({ ...t, s3_url: t.src }, i),
        }));
        setTrackCategories(buildCategories(enriched));
        setAllTracks(enriched);
        setFetchStatus('fallback');
        return enriched;
      }

      const catCounters = {};
      const enriched = active_rows.map(row => {
        const cat = row.category || 'ambience';
        catCounters[cat] = catCounters[cat] || 0;
        const mapped = mapDbTrack(row, catCounters[cat]);
        catCounters[cat]++;
        return mapped;
      });

      setTrackCategories(buildCategories(enriched));
      setAllTracks(enriched);
      setFetchStatus('ok');
      return enriched;
    } catch (err) {
      console.warn('[SoundscapePlayer] DB fetch failed, using fallback:', err.message);
      const enriched = FALLBACK_TRACKS.map((t, i) => ({
        ...t,
        ...mapDbTrack({ ...t, s3_url: t.src }, i),
      }));
      setTrackCategories(buildCategories(enriched));
      setAllTracks(enriched);
      setFetchStatus('fallback');
      return enriched;
    }
  }, []);

  /* ── On mount: fetch → resolve initial track ── */
  useEffect(() => {
    fetchTracks().then(tracks => {
      if (!tracks?.length) return;
      const prefs = loadPrefs(memorialId);
      let resolved = null;
      if (prefs?.trackId) resolved = tracks.find(t => t.id === prefs.trackId) || null;
      if (!resolved && preferred) {
        resolved = tracks.find(t =>
          t.id === preferred ||
          t.label?.toLowerCase().includes(preferred.toLowerCase()) ||
          t.category === preferred
        ) || null;
      }
      if (!resolved) resolved = tracks[0];
      setActive(resolved);
      setActiveCategory(resolved?.category || tracks[0]?.category || '');
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (trackCategories.length > 0 && !activeCategory) {
      setActiveCategory(trackCategories[0].id);
    }
  }, [trackCategories, activeCategory]);

  /* ── Cleanup ── */
  useEffect(() => () => {
    clearInterval(fadeRef.current);
    clearTimeout(volTimerRef.current);
    if (audioRef.current) { audioRef.current.pause(); audioRef.current.src = ''; }
  }, []);

  /* ── Persist prefs ── */
  useEffect(() => {
    if (active) savePrefs(memorialId, active.id, volume);
  }, [memorialId, active, volume]);

  /* ── Live volume sync ── */
  useEffect(() => {
    if (audioRef.current && isPlaying && !fadingRef.current) {
      audioRef.current.volume = volume;
    }
  }, [volume, isPlaying]);

  /* ── Fade helpers ── */
  const stopFade = useCallback(() => {
    clearInterval(fadeRef.current);
    fadeRef.current   = null;
    fadingRef.current = false;
  }, []);

  const fadeIn = useCallback((targetVol, onComplete) => {
    const el = audioRef.current;
    if (!el) { onComplete?.(); return; }
    stopFade();
    fadingRef.current = true;
    el.volume = 0;
    let v = 0;
    fadeRef.current = setInterval(() => {
      v = Math.min(v + 0.025, targetVol);
      if (audioRef.current) audioRef.current.volume = v;
      if (v >= targetVol) { stopFade(); onComplete?.(); }
    }, 50);
  }, [stopFade]);

  const fadeOut = useCallback((onDone) => {
    const el = audioRef.current;
    if (!el || el.paused) { onDone?.(); return; }
    stopFade();
    fadingRef.current = true;
    let v = el.volume;
    fadeRef.current = setInterval(() => {
      v = Math.max(v - 0.04, 0);
      if (audioRef.current) audioRef.current.volume = v;
      if (v <= 0) { stopFade(); onDone?.(); }
    }, 50);
  }, [stopFade]);

  /* ── loadAndPlay — resolves S3 URL then hands it to the <audio> element ── */
  const loadAndPlay = useCallback(async (track, targetVol) => {
    const el = audioRef.current;
    if (!el) return;
    setIsLoading(true);
    setLoadError(false);

    let playableUrl;
    try {
      playableUrl = await getPlayableUrl(track);
    } catch (err) {
      console.error('[SoundscapePlayer] URL resolution failed:', err.message);
      setLoadError(true);
      setIsLoading(false);
      return;
    }

    el.pause();
    el.src    = playableUrl;
    el.loop   = true;
    el.volume = 0;

    const onCanPlay = () => {
      el.removeEventListener('canplay', onCanPlay);
      el.play()
        .then(() => { setIsPlaying(true); setIsLoading(false); fadeIn(targetVol); })
        .catch(() => { setIsPlaying(false); setIsLoading(false); setLoadError(true); });
    };
    el.addEventListener('canplay', onCanPlay);
    el.load();
  }, [getPlayableUrl, fadeIn]);

  /* ── First-interaction autoplay ── */
  useEffect(() => {
    let triggered = false;
    const handle = () => {
      if (triggered || !active) return;
      triggered = true;
      const el = audioRef.current;
      if (!el || isPlaying) return;
      loadAndPlay(active, volumeRef.current);
    };
    window.addEventListener('click',  handle, { once: true });
    window.addEventListener('scroll', handle, { once: true });
    return () => {
      window.removeEventListener('click',  handle);
      window.removeEventListener('scroll', handle);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  /* ── switchTrack ── */
  const switchTrack = useCallback((track) => {
    if (!track || track.id === active?.id) return;
    setLoadError(false);
    const wasPlaying = isPlaying;
    const targetVol  = volumeRef.current;

    const doSwitch = () => {
      setActive(track);
      setActiveCategory(track.category);
      setIsPlaying(false);
      if (wasPlaying) loadAndPlay(track, targetVol);
    };

    wasPlaying ? fadeOut(doSwitch) : doSwitch();
  }, [active?.id, isPlaying, fadeOut, loadAndPlay]);

  /* ── togglePlay ── */
  const togglePlay = useCallback(() => {
    const el = audioRef.current;
    if (!el || !active) return;
    setLoadError(false);

    if (isPlaying) {
      fadeOut(() => {
        if (audioRef.current) audioRef.current.pause();
        setIsPlaying(false);
      });
      return;
    }

    /* Check if current audio element already has this track loaded */
    const cached = urlCache.current[active.id];
    const alreadyLoaded = cached && el.src && el.src === cached.url && !el.paused === false;

    if (alreadyLoaded && el.readyState >= 2) {
      /* Track is already buffered — just play */
      el.volume = 0;
      el.play()
        .then(() => { setIsPlaying(true); fadeIn(volumeRef.current); })
        .catch(() => { setLoadError(true); });
    } else {
      /* Need to (re)load — resolve URL first */
      loadAndPlay(active, volumeRef.current);
    }
  }, [isPlaying, active, fadeIn, fadeOut, loadAndPlay]);

  /* ── Prev / Next ── */
  const currentIndex = useMemo(
    () => allTracks.findIndex(t => t.id === active?.id),
    [allTracks, active?.id],
  );

  const goPrev = useCallback(() => {
    if (!allTracks.length) return;
    switchTrack(allTracks[(currentIndex - 1 + allTracks.length) % allTracks.length]);
  }, [currentIndex, allTracks, switchTrack]);

  const goNext = useCallback(() => {
    if (!allTracks.length) return;
    switchTrack(allTracks[(currentIndex + 1) % allTracks.length]);
  }, [currentIndex, allTracks, switchTrack]);

  /* ── Volume ── */
  const handleVolume = useCallback((v) => {
    setVolume(v);
    volumeRef.current = v;
    if (audioRef.current && !fadingRef.current) audioRef.current.volume = v;
    setSavedBadge(true);
    clearTimeout(volTimerRef.current);
    volTimerRef.current = setTimeout(() => setSavedBadge(false), 1800);
  }, []);

  /* ── Audio error ── */
  const handleError = useCallback(() => {
    const el = audioRef.current;
    console.warn('[SoundscapePlayer] Audio load error for:', el?.src || '(no src)');
    /* Invalidate cached URL so next play attempt re-fetches a fresh pre-sign */
    if (active?.id) delete urlCache.current[active.id];
    setLoadError(true);
    setIsPlaying(false);
    setIsLoading(false);
  }, [active?.id]);

  /* ── Derived ── */
  const categoryTracks = useMemo(
    () => trackCategories.find(c => c.id === activeCategory)?.tracks ?? [],
    [trackCategories, activeCategory],
  );

  /* ── Loading skeleton ── */
  if (fetchStatus === 'loading' || !active) {
    return (
      <div className="fixed bottom-6 left-4 md:left-6 z-[90]">
        <div className="w-48 h-10 bg-white/80 backdrop-blur border border-brand-dark rounded-full animate-pulse flex items-center justify-center gap-2 px-4">
          <SafeIcon icon={FiRefreshCw} className="w-3.5 h-3.5 text-text-muted animate-spin" />
          <span className="text-[9px] font-black uppercase tracking-widest text-text-muted">Loading tracks…</span>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed bottom-6 left-4 md:left-6 z-[90]">
      <audio
        ref={audioRef}
        loop
        preload="none"
        crossOrigin="anonymous"
        onError={handleError}
      />

      {/* ── Expanded panel ── */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ opacity: 0, y: 14, scale: 0.97 }}
            animate={{ opacity: 1, y: 0,  scale: 1     }}
            exit={{ opacity: 0, y: 14, scale: 0.97 }}
            transition={{ duration: 0.22 }}
            className="mb-3 bg-white/97 backdrop-blur-xl border border-brand-dark rounded-[24px] shadow-2xl shadow-accent-navy/10 overflow-hidden w-80"
          >
            {/* Header */}
            <div className="px-5 pt-5 pb-4 border-b border-brand-light">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <p className="text-[9px] font-black uppercase tracking-widest text-accent-gold">
                    Memorial Soundscape
                  </p>
                  {fetchStatus === 'fallback' && (
                    <span title="Using built-in tracks — upload custom tracks in Admin">
                      <SafeIcon icon={FiAlertCircle} className="w-3 h-3 text-amber-400" />
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <AnimatePresence>
                    {savedBadge && (
                      <motion.span
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1   }}
                        exit={{ opacity: 0, scale: 0.8    }}
                        className="flex items-center gap-1 text-[8px] font-black text-emerald-600 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5"
                      >
                        <SafeIcon icon={FiCheckCircle} className="w-2.5 h-2.5" />
                        Saved
                      </motion.span>
                    )}
                  </AnimatePresence>
                  <button
                    onClick={() => setExpanded(false)}
                    className="p-1 rounded-full hover:bg-brand-light text-text-muted transition-colors"
                  >
                    <SafeIcon icon={FiChevronDown} className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>

              <MusicPlayerWidget
                audioRef={audioRef}
                track={active}
                isPlaying={isPlaying}
                isLoading={isLoading}
                volume={volume}
                onTogglePlay={togglePlay}
                onVolume={handleVolume}
                onPrev={goPrev}
                onNext={goNext}
                trackIndex={currentIndex}
                trackTotal={allTracks.length}
                loadError={loadError}
              />
            </div>

            {/* Category tabs */}
            <div className="flex border-b border-brand-light overflow-x-auto">
              {trackCategories.map(cat => (
                <button
                  key={cat.id}
                  onClick={() => setActiveCategory(cat.id)}
                  className={`flex-1 min-w-[72px] flex flex-col items-center gap-1 py-2.5 text-[8px] font-black uppercase tracking-widest transition-all ${
                    activeCategory === cat.id
                      ? 'text-accent-navy border-b-2 border-accent-gold bg-brand-light/50'
                      : 'text-text-muted hover:text-accent-navy'
                  }`}
                >
                  <SafeIcon
                    icon={cat.icon}
                    className={`w-3.5 h-3.5 ${activeCategory === cat.id ? cat.color : ''}`}
                  />
                  {cat.label}
                </button>
              ))}
            </div>

            {/* Track list */}
            <div className="p-3 space-y-1.5 max-h-52 overflow-y-auto">
              {categoryTracks.length === 0 ? (
                <p className="text-center text-[10px] text-text-muted py-6">No tracks in this category.</p>
              ) : categoryTracks.map(track => {
                const isActiveTrack = active.id === track.id;
                return (
                  <motion.button
                    key={track.id}
                    onClick={() => switchTrack(track)}
                    whileTap={{ scale: 0.98 }}
                    className={`w-full flex items-center gap-3 p-3 rounded-2xl border-2 text-left transition-all ${
                      isActiveTrack
                        ? 'border-accent-gold bg-accent-gold/5 shadow-sm'
                        : 'border-transparent bg-brand-light hover:border-brand-dark hover:bg-white'
                    }`}
                  >
                    <div className={`w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 ${track.bg}`}>
                      {isActiveTrack && isPlaying
                        ? <EqualizerBars color={track.barColor} />
                        : <SafeIcon icon={track.icon} className={`w-4 h-4 ${track.color}`} />
                      }
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] font-black text-accent-navy leading-tight truncate">
                        {track.label}
                      </p>
                      <p className="text-[8px] text-text-muted truncate">{track.description}</p>
                    </div>
                    {isActiveTrack && (
                      <span className={`text-[8px] font-black shrink-0 ${
                        isLoading ? 'text-amber-500' : isPlaying ? 'text-accent-gold' : 'text-text-muted'
                      }`}>
                        {isLoading ? '⟳ Loading' : isPlaying ? '▶ Playing' : '■ Paused'}
                      </span>
                    )}
                  </motion.button>
                );
              })}
            </div>

            {/* Footer */}
            <div className="px-4 pb-4">
              <p className="text-[8px] text-text-muted text-center font-medium">
                {fetchStatus === 'ok'
                  ? `${allTracks.length} track${allTracks.length !== 1 ? 's' : ''} loaded from library · preferences saved ✓`
                  : 'Using built-in tracks · upload custom audio in Admin ✓'
                }
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Compact pill ── */}
      <div className="flex items-center gap-2">
        <MusicPlayerWidget
          audioRef={audioRef}
          track={active}
          isPlaying={isPlaying}
          isLoading={isLoading}
          volume={volume}
          onTogglePlay={togglePlay}
          onVolume={handleVolume}
          onPrev={goPrev}
          onNext={goNext}
          trackIndex={currentIndex}
          trackTotal={allTracks.length}
          loadError={loadError}
          compact
        />

        <motion.button
          onClick={() => setExpanded(e => !e)}
          whileTap={{ scale: 0.9 }}
          className={`w-10 h-10 rounded-full flex items-center justify-center shadow-lg border transition-all duration-200 ${
            expanded
              ? 'bg-accent-gold text-white border-accent-gold'
              : 'bg-white text-text-muted border-brand-dark hover:border-accent-gold hover:text-accent-gold'
          }`}
          title="Choose soundscape"
        >
          <SafeIcon icon={expanded ? FiChevronDown : FiChevronUp} className="w-4 h-4" />
        </motion.button>
      </div>
    </div>
  );
};

export default SoundscapePlayer;